import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { Navigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowLeftOutlined } from "@ant-design/icons";
import { skipToken } from "@reduxjs/toolkit/query";

import {
  useGetSurveyByIdQuery,
  useLazyGetSurveyStatisticsQuery,
} from "@/redux/service.js";
import SectionProgressBar from "./components/SectionProgressBar";
import {
  PRME_ORGANIZATION_ID,
  SAMPLE_SURVEY_PATH_PARAM_VALUE,
} from "@/shared/utils/constants";
import SectionContent from "./components/SectionContent.js";
import BaseButton from "@/components_v2/Button/BaseButton";
import PreviewIcon from "@/assets/icons/preview.svg?react";
import SendIcon from "@/assets/icons/send.svg?react";
import SubmissionConfirmationModal from "@/domains/survey/components/SubmissionConfirmationModal";
import BaseSvgRenderer from "@/components_v2/Svg/BaseSvgRenderer";
import { SurveyType } from "@/shared/utils/constants";
import BaseTooltip from "@/components_v2/Tooltip/BaseTooltip";
import BaseSpinner from "@/components_v2/Spiner/BaseSpinner";
import { selectCurrentUser } from "@/redux/authentication/selectors";
import {
  BUTTON_SIZES,
  BUTTON_TYPES,
  BUTTON_VARIANTS,
} from "@/components_v2/Button/constants";
import { useSurveyPermissions } from "@/features/survey/hooks/useSurveyPermissions";
import { SurveySectionFormProps } from "@/features/survey/form/types";

const SurveySectionForm = ({ surveyId, sectionId }: SurveySectionFormProps) => {
  const { t } = useTranslation();
  const location = useLocation();
  const isSample = location.pathname.includes(SAMPLE_SURVEY_PATH_PARAM_VALUE);

  const [openConfirmationModal, setOpenConfirmationModal] = useState(false);
  const { data: survey, isLoading: isSurveyLoading } = useGetSurveyByIdQuery(
    surveyId
      ? {
          surveyId,
          filters: {
            organization: isSample ? PRME_ORGANIZATION_ID : undefined,
          },
        }
      : skipToken,
  );
  const [
    getSurveyStatistics,
    { data: surveyStatistics, isLoading: isSurveyStatisticsLoading },
  ] = useLazyGetSurveyStatisticsQuery();
  const currentUser = useSelector(selectCurrentUser);
  const {
    canView,
    canSubmit,
    canEdit,
    isLoading: isLoadingPermissions,
  } = useSurveyPermissions(survey, currentUser);

  useEffect(() => {
    if (survey && canEdit) {
      getSurveyStatistics({
        survey: survey.id,
        organization: isSample ? PRME_ORGANIZATION_ID : null,
      });
    }
  }, [survey, canEdit]);

  if (isSurveyLoading || isSurveyStatisticsLoading || isLoadingPermissions) {
    return <BaseSpinner size="large" />;
  }

  if (!survey || !canView) {
    return <Navigate to="/" />;
  } else if (!canEdit) {
    return <Navigate to={`/reports/${surveyId}/overview`} />;
  }

  return (
    <div className="survey-section-form-container flex flex-col gap-200 full-width">
      <div className="flex flex-wrap gap-200 justify-between items-center">
        <BaseButton
          size={BUTTON_SIZES.SMALL}
          variant={BUTTON_VARIANTS.LINK}
          leadingIcon={<ArrowLeftOutlined />}
          to={`/reports/${surveyId}${isSample ? "/sample" : ""}`}
          type={BUTTON_TYPES.LINK}
        >
          {t("survey.labels.backToSurveyOverview", {
            surveyType: SurveyType.getLabel(survey.type),
          })}
        </BaseButton>

        {!isSample && (
          <div className="flex flex-wrap gap-200">
            <BaseButton
              size={BUTTON_SIZES.SMALL}
              variant={BUTTON_VARIANTS.TERTIARY}
              leadingIcon={
                <BaseSvgRenderer
                  width="20"
                  height="20"
                  component={PreviewIcon}
                />
              }
              to={`/reports/${survey.id}/overview`}
              type={BUTTON_TYPES.LINK}
            >
              {t("survey.labels.previewSurvey")}
            </BaseButton>
            <BaseTooltip
              title={canSubmit ? "" : t("survey.labels.onlyAdminsCanSubmit")}
            >
              <BaseButton
                size={BUTTON_SIZES.SMALL}
                disabled={!canSubmit}
                leadingIcon={
                  <BaseSvgRenderer
                    width="20"
                    height="20"
                    component={SendIcon}
                  />
                }
                onClick={() => setOpenConfirmationModal(true)}
              >
                {t("survey.labels.submitSurvey")}
              </BaseButton>
            </BaseTooltip>
          </div>
        )}
      </div>

      <div className="flex flex-col gap-500">
        {surveyStatistics && <SectionProgressBar sections={surveyStatistics} />}

        <SectionContent sectionId={sectionId} />
      </div>

      {!isSample && (
        <SubmissionConfirmationModal
          open={openConfirmationModal}
          setOpen={setOpenConfirmationModal}
          survey={survey}
        />
      )}
    </div>
  );
};

export default SurveySectionForm;
